import { FieldError } from '@civilgrid/ui/components';
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import kebabCase from 'lodash/kebabCase';
import { forwardRef, type TextareaHTMLAttributes, useEffect, useId, useRef } from 'react';
import Icon from '../Icon';
import InfoTooltip from '../InfoTooltip';
import Notify from '../Notify';
import Required from './Required';

export interface TextAreaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> {
  id?: string;
  label: string;
  value?: string | number;
  onChange?: (value: string) => void;
  error?: string;
  type?: 'string' | 'number' | 'email';
  isRequired?: boolean;
  tooltipText?: string;
  canCopy?: boolean;
  'data-testid'?: string;
  growWithContent?: boolean; // should textarea height grow with the content
  maxHeight?: number; // in pixels
  classNameOverrides?: string;
}

const TEXTAREA_PADDING = 6; // in pixels

const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      id = Math.random().toString(),
      error,
      type = 'text',
      tooltipText,
      isRequired,
      rows = 5,
      canCopy = false,
      'data-testid': testId,
      growWithContent = false,
      maxHeight,
      className,
      classNameOverrides,
      ...props
    },
    ref,
  ) => {
    const errorId = useId();
    const hasError = !isEmpty(error);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    function grow(e: HTMLTextAreaElement) {
      if (e && growWithContent) {
        e.style.height = 'auto';
        e.style.height = `${e.scrollHeight + TEXTAREA_PADDING}px`;
        if (maxHeight && e.scrollHeight > maxHeight) {
          e.style.height = `${maxHeight}px`;
        }
      }
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
    useEffect(() => {
      if (textareaRef.current) {
        grow(textareaRef.current);
      }
    }, []);

    return (
      <div className="flex w-full flex-col gap-1 font-noto">
        <div className="flex items-center gap-1 leading-snug">
          <label htmlFor={id} className="text-xs capitalize">
            <span>{label}</span>
            <Required isRequired={isRequired} />
          </label>
          {tooltipText && <InfoTooltip text={tooltipText} label={`About ${label}`} />}
        </div>
        <div className="group relative flex w-full rounded-sm">
          <textarea
            {...props}
            data-testid={testId}
            ref={(e) => {
              textareaRef.current = e;
              if (ref instanceof Function) {
                ref(e);
              } else if (ref) {
                ref.current = e;
              }
            }}
            id={id}
            aria-invalid={hasError || undefined}
            aria-describedby={[props['aria-describedby'], hasError && errorId].filter(Boolean).join(' ') || undefined}
            aria-required={isRequired || undefined}
            className={classNames(
              props.disabled && 'bg-[#efefef4d]!',
              className,
              'w-full resize-none rounded-sm border border-grey-30 p-1.5 text-xs outline-none focus:border-focus data-[has-error=true]:border-red-500',
              classNameOverrides,
            )}
            data-has-error={hasError}
            rows={rows}
            value={props.value}
            onChange={(e) => {
              props.onChange?.(e.target.value);
              grow(e.target);
            }}
          />
          {hasError && <span aria-hidden="true" className="block w-2 shrink-0 bg-red-500" />}
          {canCopy && (
            <button
              type="button"
              aria-label={label ? `Copy ${label}` : 'Copy to clipboard'}
              data-testid={testId ? `${testId}-copy` : 'textarea-copy-button'}
              className="pointer-events-none absolute right-0 flex h-full cursor-pointer appearance-none items-center rounded border-0 bg-gray-100 p-2 opacity-0 focus-ring focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100"
              onClick={() => {
                navigator.clipboard.writeText(props.value?.toString() || '');
                Notify.info({
                  message: `"${props.value?.toString()}" - copied to Clipboard`,
                });
              }}
            >
              <Icon icon="copy" size={16} />
            </button>
          )}
        </div>
        {hasError && (
          <FieldError id={errorId} className="text-xs" data-testid={`${testId ?? kebabCase(label)}-error-details`}>
            {error}
          </FieldError>
        )}
      </div>
    );
  },
);

export default TextArea;
